import { useEffect, useMemo, useRef } from 'react';
import MarkdownCopy from '@components/cms/Slices/MarkdownCopy';
import Container from '@components/global/Container';
import Button from '@components/ui/Button';
import Heading from '@components/ui/Heading';
import { useLocale } from '@hooks/useLocale';
import cn from 'classnames';
import { BrandValueSlice } from '@adapters/prismic/types/brandedStorePage';
import PrismicImage from '@components/cms/PrismicImage';
import useObjectVisibility from '@hooks/useObjectVisibility';
import {
  trackPromotionEvent,
  PromotionEventName,
  InteractionLocation,
  BrandValuesEventProps,
} from '@utils/gtm/ga4/trackPromotionEvents';
import { determinePageType, buildCreativeSlotName } from '@utils/gtm/ga4/utils';

import styles from './BrandValues.module.scss';

export interface BrandValuesProps {
  primary: BrandValueSlice['primary'];
  items: BrandValueSlice['items'];
  brandedStoreCoopAttribute?: string | null;
  sliceIndex?: number;
}

const BrandValues = ({
  primary,
  items,
  brandedStoreCoopAttribute,
  sliceIndex,
}: BrandValuesProps) => {
  const locale = useLocale();
  const BrandValue = ({
    item,
    i,
  }: {
    item: NonNullable<BrandValueSlice['items']>[0];
    i: number;
  }) => {
    const brandRef = useRef<HTMLElement>(null);
    const isVisible = useObjectVisibility(brandRef);

    const promotionDetails = useMemo(
      () => ({
        page: {
          path: typeof window !== 'undefined' ? window.location.pathname : '',
          page_type: determinePageType(),
        },
        interaction: {
          coop_brand: brandedStoreCoopAttribute || null,
          interaction_location: InteractionLocation.BrandValues,
        },
        ecommerce: {
          creative_name: item?.image_link
            ? `link,${item?.image_link}`
            : `image,${item?.image?.url}`,
          creative_slot: buildCreativeSlotName({
            sliceIndex,
            interactionLocation: InteractionLocation.BrandValues,
            itemIndex: i,
          }),
          promotion_id: item?.title || null,
          promotion_name: item?.cta_text || null,
          isVideo: false,
        },
      }),
      [i, item?.cta_text, item?.image?.url, item?.image_link, item?.title]
    );

    useEffect(() => {
      if (isVisible) {
        trackPromotionEvent({
          eventName: PromotionEventName.viewPromotion,
          ...promotionDetails,
        } as BrandValuesEventProps);
      }
    }, [isVisible, promotionDetails]);

    return (
      <article
        key={`article-item-${i}`}
        className={cn(styles.articleContainer, {
          [styles.fourItems]: primary?.number_of_cards === 'four',
          [styles.threeItems]: primary?.number_of_cards === 'three',
          [styles.twoItems]: primary?.number_of_cards === 'two',
        })}
        ref={brandRef}
      >
        <div
          style={{
            aspectRatio: item.image.dimensions
              ? `auto ${item.image.dimensions.width} / ${item.image.dimensions.height}`
              : 'auto',
          }}
          className={styles.imageContainer}
        >
          {item?.image_link ? (
            <a
              href={`/${locale}/${item?.image_link}`}
              className={styles.hasLink}
              onClick={() => {
                trackPromotionEvent({
                  eventName: PromotionEventName.selectPromotion,
                  ...promotionDetails,
                } as BrandValuesEventProps);
              }}
            >
              <PrismicImage
                src={item?.image?.url}
                alt={item.image.alt ?? ''}
                layout="fill"
              />
            </a>
          ) : (
            <PrismicImage
              src={item?.image?.url}
              alt={item.image.alt ?? ''}
              layout="fill"
            />
          )}
        </div>
        {item?.title && (
          <Heading
            semanticLevel="h4"
            styleLevel="4"
            children={item?.title}
            className={styles.heading}
          ></Heading>
        )}
        {item?.copy && (
          <MarkdownCopy
            text={item.copy?.[0]?.text ?? ''}
            className={styles.copy}
          />
        )}
        {item?.cta_text && item?.cta_url && (
          <Button
            className={styles.cta}
            label={item?.cta_text}
            size="large"
            variant="tertiary"
            type="button"
            href={`/${locale}${item?.cta_url}`}
            handleClick={() => {
              trackPromotionEvent({
                eventName: PromotionEventName.selectPromotion,
                ...promotionDetails,
                ecommerce: {
                  ...promotionDetails.ecommerce,
                  creative_name: `link,${item?.cta_url}`,
                  promotion_name: item?.cta_text ?? null,
                },
              } as BrandValuesEventProps);
            }}
          />
        )}
      </article>
    );
  };

  return (
    <div className={styles.sectionContainer} id={primary?.id}>
      <Container>
        {primary?.title && (
          <Heading
            semanticLevel="h3"
            styleLevel="1"
            children={primary?.title}
            className={styles.mainHeading}
          ></Heading>
        )}
        <div className={styles.articlesContainer}>
          {items?.map((item, i) => (
            <BrandValue key={`article-item-${i}`} item={item} i={i} />
          ))}
        </div>
      </Container>
    </div>
  );
};

export default BrandValues;
